import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { ApiResult, ErrorData } from '@synergy/api';
import { Dispatch } from 'redux';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';

import { Api } from '../../../../../../api';
import { AppState } from '../../../../../../store/models/app-state';
import { ProductFile, isImageFile } from '../../../../../../common/models/product-file';
import { ProductFiles } from '../models/product-files';

export const PRODUCT_DETAIL_FILES_FETCH = 'PRODUCT_DETAIL_FILES_FETCH';
export const PRODUCT_DETAIL_FILES_RECEIVE = 'PRODUCT_DETAIL_FILES_RECEIVE';
export const PRODUCT_DETAIL_FILES_ERROR = 'PRODUCT_DETAIL_FILES_ERROR';
export const PRODUCT_DETAIL_FILES_SET_PREVIEW_IMAGE = 'PRODUCT_DETAIL_FILES_SET_PREVIEW_IMAGE';
export const PRODUCT_DETAIL_FILES_RENAME = 'PRODUCT_DETAIL_FILES_RENAME';
export const PRODUCT_DETAIL_FILES_DELETE = 'PRODUCT_DETAIL_FILES_DELETE';

export const PRODUCT_DETAIL_FILES_UPLOAD_START = 'PRODUCT_DETAIL_FILES_UPLOAD_START';
export const PRODUCT_DETAIL_FILES_UPLOAD_PROGRESS = 'PRODUCT_DETAIL_FILES_UPLOAD_PROGRESS';
export const PRODUCT_DETAIL_FILES_UPLOAD_SUCCESS = 'PRODUCT_DETAIL_FILES_UPLOAD_SUCCESS';
export const PRODUCT_DETAIL_FILES_UPLOAD_FAILURE = 'PRODUCT_DETAIL_FILES_UPLOAD_FAIL';
export const PRODUCT_DETAIL_FILES_UPLOAD_CANCEL = 'PRODUCT_DETAIL_FILES_UPLOAD_CANCEL';

const error = (errorData?: ErrorData) => ({
    type: PRODUCT_DETAIL_FILES_ERROR,
    errorData,
});

const receiveFiles = (productFiles: ProductFiles) => ({
    type: PRODUCT_DETAIL_FILES_RECEIVE,
    productFiles,
});

const uploadFileStart = (file: ProductFile) => {
    return {
        type: PRODUCT_DETAIL_FILES_UPLOAD_START,
        file,
    };
};

const uploadFileProgress = (file: ProductFile, progress: number) => {
    return {
        type: PRODUCT_DETAIL_FILES_UPLOAD_PROGRESS,
        file,
        progress,
    };
};

const uploadFileCancel = (file: ProductFile) => {
    return {
        type: PRODUCT_DETAIL_FILES_UPLOAD_CANCEL,
        file,
    };
};

const uploadFileError = (file: ProductFile) => {
    return {
        type: PRODUCT_DETAIL_FILES_UPLOAD_FAILURE,
        file,
    };
};

const uploadFileSuccess = (file: ProductFile) => {
    return {
        type: PRODUCT_DETAIL_FILES_UPLOAD_SUCCESS,
        file,
    };
};

export const fetchProductFiles = (id: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: PRODUCT_DETAIL_FILES_FETCH });

        const response = await api.product.detail.files.getProductFiles(id);
        if (response.success) {
            dispatch(receiveFiles(response.result));
        } else {
            dispatch(error(response.error));
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

interface UploadInfo {
    file: File;
    productFile: ProductFile;
    response?: ApiResult<ProductFiles>;
}

export const addProductFiles = (files: File[]) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const productId = getState().product.detail.files.productId;
        if (!productId) {
            return;
        }

        const uploadInfos = files.map<UploadInfo>(file => ({
            file,
            productFile: {
                id: -1,
                fileName: file.name,
                path: '',
                size: file.size,
                title: file.name,
                type: '',
                uploadProgress: 1,
                isPreviewImage: false,
                formats: {},
            },
        }));
        uploadInfos.forEach(info => dispatch(uploadFileStart(info.productFile)));

        const uploadingFilePromises = uploadInfos.map(info =>
            api.product.detail.files.addProductFile(
                productId,
                info.file,
                c => (info.productFile.cancel = c),
                progress => {
                    const percentCompleted = Math.floor((progress.loaded * 100) / progress.total);
                    dispatch(uploadFileProgress(info.productFile, percentCompleted));
                }
            )
        );

        const responses = await Promise.all(uploadingFilePromises);
        responses.forEach((response, index) => (uploadInfos[index].response = response));

        // The same notice the documents give: stored the moment it is dropped (R-197).
        const saved = uploadInfos.filter(info => info.response?.success).length;
        if (saved > 0) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.upload.saved', { count: saved })));
        }

        uploadInfos.forEach(uploadInfo => {
            if (uploadInfo.response?.success) {
                const list = [...uploadInfo.response.result.images, ...uploadInfo.response.result.documents];
                const uploadedFile = list.find(
                    newFile => newFile.title === uploadInfo.file.name && newFile.size === uploadInfo.file.size
                );
                if (uploadedFile) {
                    dispatch(uploadFileSuccess(uploadedFile));
                }
            } else {
                if (uploadInfo.response?.error instanceof Error) {
                    dispatch(uploadFileError(uploadInfo.productFile));
                } else {
                    dispatch(uploadFileCancel(uploadInfo.productFile));
                }
            }
        });
    };
};

export const renameFile = (id: number, title: string) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const productId = getState().product.detail.files.productId;
        const productFile = getState().product.detail.files.files.find(file => file.id === id);
        if (!productId || !productFile) {
            return;
        }

        dispatch({ type: PRODUCT_DETAIL_FILES_RENAME, id, title });

        const response = await api.product.detail.files.patchProductFile(productId, productFile.id, productFile.type, {
            title,
        });
        if (!response.success) {
            dispatch(error(response.error));
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const setAsPreviewImage = (id: number) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const productId = getState().product.detail.files.productId;
        const productFile = getState().product.detail.files.files.find(file => file.id === id);
        if (!productId || !productFile || !isImageFile(productFile)) {
            return;
        }

        dispatch({ type: PRODUCT_DETAIL_FILES_SET_PREVIEW_IMAGE, id });

        const response = await api.product.detail.files.patchProductFile(productId, productFile.id, productFile.type, {
            isPreviewImage: true,
        });
        if (!response.success) {
            dispatch(error(response.error));
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const deleteProductFile = (file: ProductFile) => {
    const id = file.id;

    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const productId = getState().product.detail.files.productId;
        if (!productId) {
            return;
        }

        dispatch({ type: PRODUCT_DETAIL_FILES_DELETE, id });

        const response = await api.product.detail.files.deleteProductFile(productId, id, file.type);

        if (!response.success) {
            dispatch(error(response.error));
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};
